import axios from 'axios';
import { useAuthStore } from '../stores/authStore';
import { mockApi, USE_MOCK_API } from './mockApi';
import type { 
  InvoicesResponse, 
  MetricsSummary,
  DashboardMetrics, 
  KeyStatus,
  DashboardKeyStatus, 
  RetryResponse, 
  LoginRequest, 
  AuthResponse,
  ExportResponse
} from '../types/api';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      useAuthStore.getState().logout();
    }
    return Promise.reject(error);
  }
);

// Adapter functions to convert backend responses to dashboard-compatible format
function adaptMetricsToLegacy(backendMetrics: MetricsSummary): DashboardMetrics {
  // Extract trend data for 24h volume
  const hourlyData = backendMetrics.daily.trend_data 
    ? backendMetrics.daily.trend_data.map((point) => {
        const hour = new Date(point.date).getHours();
        return {
          hour: String(hour).padStart(2, '0') + ':00',
          count: point.total
        };
      })
    : [];
  
  // Ensure we have data for all 24 hours (some hours might be missing if there's no data)
  const volume24h = Array.from({ length: 24 }, (_, i) => {
    const existing = hourlyData.find((item) => item.hour === String(i).padStart(2, '0') + ':00');
    return existing || { hour: String(i).padStart(2, '0') + ':00', count: 0 };
  });

  return {
    today_processed: backendMetrics.daily.total_invoices,
    today_failed: backendMetrics.daily.failed_count,
    today_avg_latency_ms: 150, // We don't have real latency data yet
    last_failure: backendMetrics.daily.failed_count > 0 && backendMetrics.daily.error_breakdown ? {
      id: 'INV-LATEST',
      time: new Date().toISOString(),
      reason: Object.keys(backendMetrics.daily.error_breakdown)[0] || 'Unknown'
    } : undefined,
    volume_24h: volume24h
  };
}

function adaptKeyStatusToLegacy(backendKeyStatus: KeyStatus): DashboardKeyStatus {
  // Convert new key status format to legacy format
  const activeKey = backendKeyStatus.keys.find(key => key.status === 'active');
  const daysUntilExpiry = activeKey?.expires_at 
    ? Math.ceil((new Date(activeKey.expires_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : 90;
    
  return {
    jwt_expires_in_days: daysUntilExpiry,
    client_cert_expires_in_days: daysUntilExpiry,
    vault_hsm: backendKeyStatus.active_keys > 0 ? 'healthy' : 'unhealthy'
  };
}

// Create API object that switches between mock and real API based on environment
const createApi = () => {
  if (USE_MOCK_API) {
    console.log('🎭 Using Mock API mode for development');
    return {      
      // Authentication
      login: mockApi.login,
      logout: async (): Promise<void> => {
        // Mock logout
        await new Promise(resolve => setTimeout(resolve, 200));
      },

      // Invoices  
      getInvoices: mockApi.getInvoices,
      getInvoice: async (uuid: string) => {
        // Mock single invoice fetch
        const invoices = await mockApi.getInvoices();
        const invoice = invoices.data.find(inv => inv.uuid === uuid);
        if (!invoice) throw new Error('Invoice not found');
        return invoice;
      },
      retryInvoice: mockApi.retryInvoice,

      // Metrics - return legacy format for dashboard compatibility
      getMetrics: mockApi.getMetrics,

      // Keys - return legacy format for dashboard compatibility
      getKeyStatus: mockApi.getKeysStatus,

      // Export
      exportInvoices: mockApi.exportInvoices,

      // Health
      getHealth: async () => {
        await new Promise(resolve => setTimeout(resolve, 100));
        return { status: 'ok', timestamp: new Date().toISOString() };
      },
    };
  }

  // Real API implementation
  return {    
    // Authentication
    async login(credentials: LoginRequest): Promise<AuthResponse> {
      const { data } = await apiClient.post('/v1/auth/login', credentials);
      // Store the token in localStorage
      if (data.token) {
        localStorage.setItem('authToken', data.token);
      }
      return data;
    },

    async logout(): Promise<void> {
      localStorage.removeItem('authToken');
      // Note: Backend doesn't have logout endpoint yet, just clear local token
    },

    // Invoices
    async getInvoices(params?: {
      page?: number;
      per_page?: number;
      country?: string;
      status?: string;
      start_date?: string;
      end_date?: string;
    }): Promise<InvoicesResponse> {
      const { data } = await apiClient.get('/v1/invoices', { params });
      return data;
    },

    async getInvoice(uuid: string) {
      const { data } = await apiClient.get(`/v1/invoices/${uuid}`);
      return data;
    },

    async retryInvoice(uuid: string): Promise<RetryResponse> {
      const { data } = await apiClient.post(`/v1/invoices/${uuid}/retry`);
      return data;
    },

    // Metrics - adapt backend response to legacy dashboard format
    async getMetrics(): Promise<DashboardMetrics> {
      const { data } = await apiClient.get('/v1/metrics/summary');
      return adaptMetricsToLegacy(data);
    },

    // Keys - adapt backend response to legacy dashboard format
    async getKeyStatus(): Promise<DashboardKeyStatus> {
      const { data } = await apiClient.get('/v1/keys/status');
      return adaptKeyStatusToLegacy(data);
    },    
    
    // Export
    async exportInvoices(params: {
      format: 'csv' | 'zip';
      status?: string;
      country?: string;
      start_date?: string;
      end_date?: string;
    }): Promise<ExportResponse> {
      const { data } = await apiClient.post('/v1/invoices/export', params);
      return data;
    },

    // Health
    async getHealth() {
      const { data } = await apiClient.get('/v1/health');
      return data;
    },
  };
};

export const api = createApi();
